/**
 * underwater_life — what hangs in the water around a diver (R12 c/d of the
 * 2026-09-25 water-realism wave): MARINE SNOW (a camera-local box of slow
 * motes drifting on a current — the cue that tells the eye it is inside a
 * medium, not behind a tinted glass) and BUBBLES (a burst on the dive, an
 * exhale every few seconds while the head is under, a trickle while swimming;
 * they rise wobbling and pop on the surface into the ripple field).
 *
 * The engine emitters (GPU compute on WebGPU, the CPU adapter on WebGL 2 —
 * which must be RENDERED each frame: its billboards are written by
 * `render(camera)`, never by `update`). THE FRAME LAW: GPU kernels spawn in
 * RENDER space (absolute + scene.position), the CPU adapter in ABSOLUTE.
 * Premultiplied alpha (the weather's lesson). Emission runs only submerged.
 */
import * as THREE from 'three/webgpu';
import type { App } from 'enginex';
import { GpuParticleEmitter, CpuParticleAdapter } from 'enginex/particles/gpu';
import { stampWater, waterFx } from './water_fx.js';
import { atlas } from '../state.js';
import { disposeLater } from './gpu_dispose.js';

type Emitter = GpuParticleEmitter | CpuParticleAdapter;

/** Marine snow: motes alive around the camera at once (≈ rate × life). */
export const SNOW_RATE = 260;
export const SNOW_LIFE: [number, number] = [6, 9];
/** Half-size of the camera-local box the motes spawn in (m). */
export const SNOW_BOX_M = 9;
/** Bubbles rise at ~this speed (m/s) — a ~5 mm bubble's terminal velocity is ~0.25–0.3 m/s. */
export const BUBBLE_RISE_MPS = 0.55;
/** An exhale every this many seconds while the head is under. */
export const EXHALE_S = 4.2;
/** Bubbles on the dive, per exhale, per second of stroking. */
export const BUBBLES_DIVE = 70;
export const BUBBLES_EXHALE = 14;
export const BUBBLES_STROKE_PER_S = 9;

/** When a bubble released `depth` m down reaches the surface (s) — the pop is scheduled then. */
export function bubbleSurfaceDelay(depth: number): number { return Math.max(0.2, depth) / BUBBLE_RISE_MPS; }

export class UnderwaterLife {
  private readonly snow: Emitter;
  private readonly bubbles: Emitter;
  private readonly scene: THREE.Scene;
  private readonly app: App;
  private readonly pos = new THREE.Vector3();
  private readonly lastCam = new THREE.Vector3(NaN, 0, 0);
  private exhaleT = 0;
  private strokeDebt = 0;
  private wasUnder = false;
  private readonly pops: Array<{ at: number; x: number; z: number }> = [];
  private clock = 0;
  private readonly counters = { dives: 0, exhales: 0, bubbles: 0, pops: 0, snowAlive: 0, bubblesAlive: 0 };

  constructor(app: App, scene: THREE.Scene) {
    this.app = app;
    this.scene = scene;
    const mk = (cfg: Record<string, unknown>): Emitter =>
      app.renderer.isWebGPU ? new GpuParticleEmitter(app.renderer as never, cfg as never) : new CpuParticleAdapter(cfg as never);
    this.snow = mk({
      maxParticles: 2600, lifetime: { min: SNOW_LIFE[0], max: SNOW_LIFE[1] }, startSpeed: { min: 0.02, max: 0.1 },
      startSize: { min: 0.012, max: 0.035 }, startColor: 0xd4e6d8, simulationSpace: 'world',
      shape: { type: 'box', halfExtents: new THREE.Vector3(SNOW_BOX_M, SNOW_BOX_M * 0.7, SNOW_BOX_M) },
      emission: { rateOverTime: 0 }, forces: { gravityModifier: 0.004, drag: 0.6, curl: { strength: 0.12, frequency: 0.25, scrollSpeed: 0.08 } },
      blending: THREE.NormalBlending, depthWrite: false,
      alphaOverLifetime: [{ time: 0, value: 0 }, { time: 0.2, value: 0.45 }, { time: 0.8, value: 0.45 }, { time: 1, value: 0 }],
    });
    this.bubbles = mk({
      maxParticles: 600, lifetime: { min: 2, max: 6 }, startSpeed: { min: 0.15, max: 0.5 }, startSize: { min: 0.02, max: 0.07 },
      startColor: 0xe8f6ff, simulationSpace: 'world', shape: { type: 'sphere', radius: 0.22 },
      emission: { rateOverTime: 0 }, forces: { gravityModifier: -0.06, drag: 1.4, curl: { strength: 0.9, frequency: 1.6, scrollSpeed: 0.9 } },
      blending: THREE.NormalBlending, depthWrite: false,
      sizeOverLifetime: [{ time: 0, value: 0.7 }, { time: 1, value: 1.25 }],
      alphaOverLifetime: [{ time: 0, value: 0.85 }, { time: 0.85, value: 0.7 }, { time: 1, value: 0 }],
    });
    for (const e of [this.snow, this.bubbles]) {
      e.mesh.frustumCulled = false;
      e.mesh.renderOrder = 6;
      const m = e.mesh.material as THREE.Material & { premultipliedAlpha?: boolean };
      m.premultipliedAlpha = true;
      scene.add(e.mesh);
    }
  }

  /** The dive: a burst of bubbles around the head (absolute coords). */
  dive(x: number, y: number, z: number): void {
    this.counters.dives++;
    this.burst(x, y - 0.2, z, BUBBLES_DIVE, 1.2);
  }

  private seat(e: Emitter, x: number, y: number, z: number): void {
    const sp = e.backend === 'gpu' ? this.scene.position : { x: 0, y: 0, z: 0 };
    this.pos.set(x + sp.x, y + sp.y, z + sp.z);
    e.worldMatrix.makeTranslation(this.pos.x, this.pos.y, this.pos.z);
  }

  private burst(x: number, y: number, z: number, n: number, depthForPop: number): void {
    this.seat(this.bubbles, x, y, z);
    this.bubbles.emit(n);
    this.counters.bubbles += n;
    this.pops.push({ at: this.clock + bubbleSurfaceDelay(depthForPop), x, z });
  }

  /** Per frame. `cam` absolute; `surfaceY` the water's level over the camera (null when not under). */
  update(dt: number, under: boolean, cam: THREE.Vector3, surfaceY: number | null, _scenePos: THREE.Vector3): void {
    const d = Math.min(0.1, Math.max(0, dt));
    this.clock += d;
    const depth = surfaceY !== null ? surfaceY - cam.y : 0;
    // Marine snow follows the camera; it only spawns while under.
    this.seat(this.snow, cam.x, cam.y, cam.z);
    this.snow.setEmissionRate(under ? SNOW_RATE : 0);
    if (under) {
      if (!this.wasUnder) this.exhaleT = EXHALE_S * 0.6;
      // Exhale while the head is under (the breath meter's own flag).
      if (atlas.headUnder) {
        this.exhaleT -= d;
        if (this.exhaleT <= 0) { this.exhaleT = EXHALE_S * (0.8 + Math.random() * 0.4); this.counters.exhales++; this.burst(cam.x, cam.y - 0.1, cam.z, BUBBLES_EXHALE, depth); }
      }
      // Stroke trickle: bubbles off the body while it moves.
      if (Number.isFinite(this.lastCam.x) && d > 0) {
        const v = Math.hypot(cam.x - this.lastCam.x, cam.y - this.lastCam.y, cam.z - this.lastCam.z) / d;
        if (v > 0.6 && v < 12) {
          this.strokeDebt += BUBBLES_STROKE_PER_S * d;
          if (this.strokeDebt >= 3) { const n = Math.floor(this.strokeDebt); this.strokeDebt -= n; this.burst(cam.x, cam.y - 0.9, cam.z, n, depth + 0.9); }
        }
      }
    }
    this.wasUnder = under;
    this.lastCam.copy(cam);
    this.snow.update(d);
    this.bubbles.update(d);
    // The CPU adapter billboards only when rendered (WebGL 2).
    if (this.snow.backend === 'cpu' || this.bubbles.backend === 'cpu') {
      let camera: THREE.Camera | undefined;
      try { camera = this.app.renderer.cameras.main; } catch { camera = undefined; }
      if (camera) {
        if (this.snow.backend === 'cpu') (this.snow as CpuParticleAdapter).render(camera);
        if (this.bubbles.backend === 'cpu') (this.bubbles as CpuParticleAdapter).render(camera);
      }
    }
    // Pops: a bubble cluster that reaches the surface rings the interaction field.
    for (let i = this.pops.length - 1; i >= 0; i--) {
      const p = this.pops[i];
      if (p.at > this.clock) continue;
      this.pops.splice(i, 1);
      if (waterFx().enabled) { stampWater(p.x, p.z, -0.03, 0.35); this.counters.pops++; }
    }
    this.counters.snowAlive = this.snow.getActiveCount();
    this.counters.bubblesAlive = this.bubbles.getActiveCount();
  }

  stats(): object { return { ...this.counters, snowBackend: this.snow.backend, pending: this.pops.length }; }

  dispose(): void {
    // Deferred (the frame-safety law): the emitters were bound in the frame in flight.
    for (const e of [this.snow, this.bubbles]) { this.scene.remove(e.mesh); disposeLater(e as never); }
  }
}
